import { type ReactNode } from 'react';
import classNames from 'classnames';
import {
  Body1,
  Body1Strong,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Divider,
  Subtitle2,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import AvatarGroup from '@modules/users/components/AvatarGroup';
import { useUsers } from '@modules/users/api/users.api';
import { DateLabel } from '@shared/components/dates/DateLabel';
import TintBadge from '@shared/components/TintBadge';
import { useFocusRestore } from '@shared/hooks';
import { GATE_DECISION_METADATA } from '../constants';
import { isChecklistComplete } from '../helpers';
import useProjectStages from '../hooks/useProjectStages';
import useProjectStagesStore from '../stores/useProjectStagesStore';
import { EGateDecision, EStageStatus } from '../types';
import StageStatusBadge from './StageStatusBadge';

const Detail = ({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) => (
  <div className={classNames('flex min-w-0 flex-col gap-1.5', className)}>
    <Body1>{label}</Body1>
    {children}
  </div>
);

const StageDialog = ({ projectId }: { projectId: string }) => {
  const { stages, updateGate } = useProjectStages(projectId);
  const storedState = useProjectStagesStore((state) => state.stageDialogState);
  const setStageDialogState = useProjectStagesStore((state) => state.setStageDialogState);
  const state = storedState?.projectId === projectId ? storedState : undefined;
  useFocusRestore(state?.isOpen ?? false);
  const { userMap } = useUsers();
  const stage = state && stages[state.stageIndex];
  if (!state || !stage) return null;
  const { gate } = stage;
  const gateState = stage.gateState ?? { checked: [] };
  const checklist = gate?.checklist ?? [];
  const isCurrent = stage.status === EStageStatus.CURRENT;
  return (
    <Dialog open={state.isOpen} onOpenChange={(_, data) => setStageDialogState({ ...state, isOpen: data.open })}>
      <DialogSurface className="!max-w-[720px]">
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={() => setStageDialogState({ ...state, isOpen: false })}
              />
            }
          >
            {stage.title}
          </DialogTitle>
          <DialogContent className="max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <Detail label="Status">
                  <div>
                    <StageStatusBadge status={stage.status} />
                  </div>
                </Detail>
                <Detail label="Start date">
                  <Body1Strong>{stage.startDate ? <DateLabel>{stage.startDate}</DateLabel> : '-'}</Body1Strong>
                </Detail>
                <Detail label="End date">
                  <Body1Strong>{stage.endDate ? <DateLabel>{stage.endDate}</DateLabel> : '-'}</Body1Strong>
                </Detail>
              </div>
              <Divider />
              <Subtitle2>Gate</Subtitle2>
              {gate ? (
                <>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                    <Detail label="Title" className="sm:col-span-2">
                      <Body1Strong className="break-words">{gate.title}</Body1Strong>
                    </Detail>
                    <Detail label="Gatekeeper">
                      {gate.gatekeeper ? (
                        <div className="flex items-center gap-2">
                          <AvatarGroup ids={[gate.gatekeeper]} />
                          <Body1Strong className="min-w-0 break-words">
                            {userMap?.[gate.gatekeeper]?.name ?? '-'}
                          </Body1Strong>
                        </div>
                      ) : (
                        <Body1Strong>-</Body1Strong>
                      )}
                    </Detail>
                    <Detail label="Description" className="sm:col-span-3">
                      <Body1 className="!whitespace-pre-wrap break-words">{gate.description || '-'}</Body1>
                    </Detail>
                  </div>
                  <Detail
                    label={
                      checklist.length
                        ? `Checklist (${gateState.checked.length} of ${checklist.length} checked)`
                        : 'Checklist'
                    }
                  >
                    {checklist.length ? (
                      <div className="flex flex-col">
                        {checklist.map((item, index) => (
                          <Checkbox
                            key={index}
                            label={item}
                            checked={gateState.checked.includes(index)}
                            disabled={!isCurrent}
                            onChange={(_, data) =>
                              updateGate(stage.index, {
                                checked: data.checked
                                  ? [...gateState.checked, index]
                                  : gateState.checked.filter((checked) => checked !== index),
                              })
                            }
                          />
                        ))}
                      </div>
                    ) : (
                      <Body1Strong>-</Body1Strong>
                    )}
                  </Detail>
                  <Detail
                    label={
                      <span className="flex items-center gap-2">
                        Decision
                        {gateState.decision && (
                          <TintBadge color={GATE_DECISION_METADATA[gateState.decision].color}>
                            {GATE_DECISION_METADATA[gateState.decision].decisionLabel}
                          </TintBadge>
                        )}
                      </span>
                    }
                  >
                    <div className="flex flex-wrap gap-2">
                      {Object.values(EGateDecision).map((decision) => {
                        const { label, icon: Icon } = GATE_DECISION_METADATA[decision];
                        return (
                          <Button
                            key={decision}
                            icon={<Icon />}
                            appearance={decision === EGateDecision.ACCEPT ? 'primary' : 'secondary'}
                            disabled={
                              !isCurrent ||
                              gateState.decision === decision ||
                              (decision === EGateDecision.ACCEPT && !isChecklistComplete(stage))
                            }
                            onClick={() => updateGate(stage.index, { decision })}
                          >
                            {label}
                          </Button>
                        );
                      })}
                    </div>
                  </Detail>
                </>
              ) : (
                <Body1>-</Body1>
              )}
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default StageDialog;
